import React, { useState, useEffect, useCallback } from 'react';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdRefresh, MdEmail, MdPhone, MdPerson } from 'react-icons/md';
import { toast } from 'react-toastify';
import SkeletonRow from '../components/SkeletonRow';
import axiosInstance from '../../../api/axiosInstance';
import '../Admin.css';

const COORDINATORS_BASE_URL = '/Corrohealth/api/coordinators';

function CoordinatorManagement() {
  const [coordinators, setCoordinators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCoordinator, setEditingCoordinator] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [workloadSummary, setWorkloadSummary] = useState([]);

  const loadCoordinators = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(COORDINATORS_BASE_URL);
      const data = response.data;
      if (data.success !== false) {
        setCoordinators(data.data?.coordinators || []);
      } else {
        toast.error(data.message || 'Failed to load coordinators');
      }
    } catch (error) {
      console.error('Error loading coordinators:', error);
      toast.error('Failed to load coordinators');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadWorkloadSummary = useCallback(async () => {
    try {
      const response = await axiosInstance.get(`${COORDINATORS_BASE_URL}/workload`);
      const data = response.data;
      if (data.success !== false) {
        setWorkloadSummary(data.data?.workload || []);
      }
    } catch (error) {
      console.error('Error loading workload summary:', error);
    }
  }, []);

  useEffect(() => {
    loadCoordinators();
    loadWorkloadSummary();
  }, [loadCoordinators, loadWorkloadSummary]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    setEditingCoordinator(null);
    setFormData({ name: '', email: '', phone: '' });
    setShowModal(true);
  };

  const openEditModal = (coordinator) => {
    setEditingCoordinator(coordinator);
    setFormData({
      name: coordinator.name || '',
      email: coordinator.email || '',
      phone: coordinator.phone || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCoordinator(null);
    setFormData({ name: '', email: '', phone: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Coordinator name is required');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Coordinator email is required');
      return;
    }

    setSaving(true);
    try {
      let response;
      if (editingCoordinator) {
        response = await axiosInstance.put(`${COORDINATORS_BASE_URL}/${editingCoordinator.id}`, formData);
      } else {
        response = await axiosInstance.post(COORDINATORS_BASE_URL, formData);
      }

      const data = response.data;

      if (data.success !== false) {
        toast.success(editingCoordinator ? 'Coordinator updated successfully' : 'Coordinator added successfully');
        closeModal();
        loadCoordinators();
        loadWorkloadSummary();
      } else {
        toast.error(data.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Error saving coordinator:', error);
      toast.error(error.response?.data?.message || 'Failed to save coordinator');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (coordinator) => {
    if (!window.confirm(`Are you sure you want to delete coordinator "${coordinator.name}"?`)) {
      return;
    }

    try {
      const response = await axiosInstance.delete(`${COORDINATORS_BASE_URL}/${coordinator.id}`);
      const data = response.data;

      if (data.success !== false) {
        toast.success('Coordinator deleted successfully');
        loadCoordinators();
        loadWorkloadSummary();
      } else {
        toast.error(data.message || 'Delete failed');
      }
    } catch (error) {
      console.error('Error deleting coordinator:', error);
      toast.error(error.response?.data?.message || 'Failed to delete coordinator');
    }
  };

  const filteredCoordinators = coordinators.filter(c =>
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-left">
          <h1>Coordinator Management</h1>
          <p className="subtitle">Add and manage interview coordinators (email-based)</p>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={openAddModal}>
            <MdAdd /> Add Coordinator
          </button>
        </div>
      </div>

      {/* Workload Summary Cards */}
      <div className="summary-cards" style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div className="summary-card" style={{ background: '#f0fdf4', padding: '16px 24px', borderRadius: '8px', minWidth: '180px' }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#16a34a' }}>{coordinators.length}</div>
          <div style={{ color: '#166534', fontSize: '14px' }}>Total Coordinators</div>
        </div>
        <div className="summary-card" style={{ background: '#eff6ff', padding: '16px 24px', borderRadius: '8px', minWidth: '180px' }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#2563eb' }}>
            {workloadSummary.reduce((sum, w) => sum + (w.activeAssignments || 0), 0)}
          </div>
          <div style={{ color: '#1e40af', fontSize: '14px' }}>Active Assignments</div>
        </div>
        <div className="summary-card" style={{ background: '#faf5ff', padding: '16px 24px', borderRadius: '8px', minWidth: '180px' }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#7c3aed' }}>
            {workloadSummary.reduce((sum, w) => sum + (w.totalAssignments || 0), 0)}
          </div>
          <div style={{ color: '#5b21b6', fontSize: '14px' }}>Total Assignments</div>
        </div>
      </div>

      {/* Search and Actions */}
      <div className="table-controls" style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
        <div className="search-box" style={{ flex: 1, maxWidth: '400px', position: 'relative' }}>
          <MdSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: '100%', padding: '10px 12px 10px 40px', border: '1px solid #e5e7eb', borderRadius: '6px' }}
          />
        </div>
        <button className="btn-secondary" onClick={() => { loadCoordinators(); loadWorkloadSummary(); }} title="Refresh">
          <MdRefresh />
        </button>
      </div>

      {/* Coordinators Table */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Active Assignments</th>
              <th>Total Assignments</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRow columns={6} rows={5} />
            ) : filteredCoordinators.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                  {searchQuery ? 'No coordinators found matching your search' : 'No coordinators added yet. Click "Add Coordinator" to get started.'}
                </td>
              </tr>
            ) : (
              filteredCoordinators.map(coordinator => {
                const workload = workloadSummary.find(w => w.id === coordinator.id) || {};
                return (
                  <tr key={coordinator.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#7c3aed', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                          {coordinator.name?.charAt(0)?.toUpperCase() || 'C'}
                        </div>
                        <span style={{ fontWeight: 500 }}>{coordinator.name}</span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280' }}>
                        <MdEmail size={16} />
                        {coordinator.email}
                      </div>
                    </td>
                    <td>
                      {coordinator.phone ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#6b7280' }}>
                          <MdPhone size={16} />
                          {coordinator.phone}
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>-</span>
                      )}
                    </td>
                    <td>
                      <span style={{
                        background: (workload.activeAssignments || 0) > 0 ? '#fef3c7' : '#f3f4f6',
                        color: (workload.activeAssignments || 0) > 0 ? '#92400e' : '#6b7280',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: 500
                      }}>
                        {workload.activeAssignments || 0}
                      </span>
                    </td>
                    <td style={{ color: '#6b7280' }}>{workload.totalAssignments || 0}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="btn-icon"
                          onClick={() => openEditModal(coordinator)}
                          title="Edit"
                          style={{ padding: '6px', background: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          <MdEdit size={18} />
                        </button>
                        <button
                          className="btn-icon"
                          onClick={() => handleDelete(coordinator)}
                          title="Delete"
                          style={{ padding: '6px', background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          <MdDelete size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>{editingCoordinator ? 'Edit Coordinator' : 'Add New Coordinator'}</h3>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ padding: '20px' }}>
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label htmlFor="name" style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
                    <MdPerson style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter coordinator name"
                    required
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label htmlFor="email" style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
                    <MdEmail style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    required
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                  />
                  <small style={{ color: '#6b7280', marginTop: '4px', display: 'block' }}>
                    Interview notifications will be sent to this email
                  </small>
                </div>
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label htmlFor="phone" style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>
                    <MdPhone style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                    Phone (Optional)
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: '6px' }}
                  />
                </div>
              </div>
              <div className="modal-footer" style={{ padding: '16px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button type="button" className="btn-secondary" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : (editingCoordinator ? 'Update' : 'Add Coordinator')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CoordinatorManagement;
